import React from "react";
import { Compass, HeartHandshake, Sprout, Layers, Users, Briefcase } from "lucide-react";
import { SectionTitle, Reveal, Prose, PETROL } from "@/components/home/homeUi";

const AREAS = [
  { id: "onmagad", Icon: Compass, title: "Önmagadhoz való kapcsolódás", text: "Önazonosság, belső értékek, saját döntések, érzékelés és belső iránytű." },
  { id: "parkapcsolat", Icon: HeartHandshake, title: "Párkapcsolat és férfi–női dinamikák", text: "Kölcsönösség, közös irány, tiszta kommunikáció, női és férfi minőségek, egymás támogatása." },
  { id: "szulok-gyermekek", Icon: Sprout, title: "Szülők és gyermekek kapcsolata", text: "Nevelés, kontroll, önállóság, határok, tisztelet és a családi együttműködés új lehetőségei." },
  { id: "generaciok", Icon: Layers, title: "Generációkon átívelő családi működések", text: "Ránk rakódott szerepek, ismétlődő minták, elvárások, tekintély és családi hierarchiák." },
  { id: "baratsagok", Icon: Users, title: "Barátságok és választott közösségek", text: "Bizalom, kölcsönösség, kapcsolódás, határhúzás és közös értékek." },
  { id: "munkahely", Icon: Briefcase, title: "Munkahelyi és társadalmi kapcsolódások", text: "Hatalom, versengés, együttműködés, kommunikáció, vezetői és alárendeltségi működések." },
];

function AreaCard({ area, index }) {
  const { Icon } = area;
  return (
    <Reveal delay={(index % 3) * 0.1} className="h-full">
      <article
        data-testid={`home-area-card-${area.id}`}
        className="group h-full bg-warm border border-hairline hover:border-[var(--accent-gold)] px-7 md:px-8 py-9 md:py-10 text-center transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1"
        style={{ boxShadow: "0 12px 40px -34px rgba(160, 122, 40, 0.35)" }}
      >
        <span
          aria-hidden="true"
          className="mx-auto mb-6 w-14 h-14 rounded-full flex items-center justify-center border transition-colors duration-500"
          style={{ borderColor: `${PETROL}33` }}
        >
          <Icon size={22} strokeWidth={1.2} className="text-gold" />
        </span>
        <h3 className="font-serif-display text-[22px] md:text-[24px] leading-[1.3] text-graphite mb-4">
          {area.title}
        </h3>
        <p className="font-body font-light text-graphite-soft text-[15px] md:text-[15.5px] leading-[1.8]">
          {area.text}
        </p>
      </article>
    </Reveal>
  );
}

export default function HomeConnectionAreas() {
  return (
    <section
      id="kapcsolodasi-teruletek"
      data-testid="home-connection-areas"
      className="px-5 md:px-10 py-10 md:py-14 border-t border-hairline"
    >
      <SectionTitle testId="home-connection-areas-title" className="mb-8 md:mb-10">
        Milyen kapcsolódásokat érinthet a
        <br />
        <em className="text-gold" style={{ fontStyle: "italic" }}>közös folyamat?</em>
      </SectionTitle>
      <Prose className="mb-8 md:mb-10">
        <p data-testid="home-connection-areas-intro">
          Amikor családi dinamikákról beszélünk, nem kizárólag a vér szerinti család
          működésére gondolok. Az emberi kapcsolódások sokkal szélesebb rendszerében
          élünk, amelynek különböző területein hasonló működések jelenhetnek meg.
        </p>
      </Prose>
      <Reveal className="max-w-2xl mx-auto mb-9 md:mb-11">
        <figure className="portrait-frame">
          <img
            data-testid="home-connection-areas-image"
            src="/images/forras-kod-highlight.jpg"
            alt="Forrás-Kód® Kísérés — csoportos beszélgetés, őszinte kapcsolódás"
            className="relative z-10 w-full h-auto block"
            style={{ filter: "saturate(0.96) contrast(1.02)" }}
            loading="lazy"
          />
        </figure>
      </Reveal>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {AREAS.map((a, i) => (
          <AreaCard key={a.id} area={a} index={i} />
        ))}
      </div>
      <Reveal className="mt-9 md:mt-11 text-center">
        <p
          data-testid="home-connection-areas-closing"
          className="font-serif-display italic text-graphite text-[20px] sm:text-[22px] md:text-[25px] leading-[1.55] max-w-2xl mx-auto"
        >
          A közös pont minden esetben az ember, és az a működés,
          <br className="hidden sm:block" />{" "}
          amelyet a kapcsolataiba visz.
        </p>
      </Reveal>
    </section>
  );
}
